import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminDashboard.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { commonrequest } from '../../services/ApiCall';
import { BACKEND_URL } from '../../services/Helper';

// ── Cloudinary upload ───────────────────────────────────────────────────────
async function uploadToCloudinary(file) {
  const cloud = process.env.REACT_APP_CLOUDINARY_CLOUD;
  const preset = process.env.REACT_APP_CLOUDINARY_PRESET;
  const data = new FormData();
  data.append('file', file);
  data.append('upload_preset', preset);
  data.append('cloud_name', cloud);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${cloud}/image/upload`, { method: 'POST', body: data });
  if (!res.ok) throw new Error('Upload failed');
  const json = await res.json();
  return json.secure_url;
}

// ── Google Maps link parser ──────────────────────────────────────────────────
function parseGoogleMapsLink(url) {
  const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (atMatch) return { lat: atMatch[1], lng: atMatch[2] };
  const qMatch = url.match(/[?&](?:q|ll)=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (qMatch) return { lat: qMatch[1], lng: qMatch[2] };
  return null;
}

// ── ImageUpload sub-component ────────────────────────────────────────────────
function ImageUpload({ value, onChange, label = 'Image' }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const fieldStyle = { gridColumn: '1 / -1' };
  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: '#1F2937', color: '#F9FAFB', fontSize: 13, boxSizing: 'border-box' };
  const btnStyle = { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563EB', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      onChange(url);
    } catch {
      toast.error('Image upload failed. You can paste a URL instead.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="admin-modal-field" style={fieldStyle}>
      <label>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {value ? (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={value} alt="preview" style={{ width: 160, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid #374151', display: 'block' }} />
            <button type="button" onClick={() => onChange('')} style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={() => fileRef.current?.click()} style={btnStyle} disabled={uploading}>
            {uploading ? 'Uploading...' : '📷 Upload Image'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>or paste URL below</span>
        </div>
        <input type="text" placeholder="Image URL (optional fallback)" value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle} />
      </div>
    </div>
  );
}

// ── LocationPicker sub-component ─────────────────────────────────────────────
function LocationPicker({ lat, lng, onChangeLat, onChangeLng }) {
  const [linkInput, setLinkInput] = useState('');
  const [locLoading, setLocLoading] = useState(false);
  const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: '#1F2937', color: '#F9FAFB', fontSize: 13 };
  const btnStyle = { padding: '8px 14px', borderRadius: 8, border: 'none', background: '#374151', color: '#F9FAFB', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' };

  const handleGPS = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported by your browser');
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => { onChangeLat(String(pos.coords.latitude.toFixed(6))); onChangeLng(String(pos.coords.longitude.toFixed(6))); setLocLoading(false); },
      () => { toast.error('Location access denied. Allow location in browser settings.'); setLocLoading(false); }
    );
  };

  const handleLink = () => {
    const result = parseGoogleMapsLink(linkInput);
    if (result) { onChangeLat(result.lat); onChangeLng(result.lng); setLinkInput(''); toast.success('Coordinates extracted'); }
    else toast.error('Could not extract coordinates. Try: Google Maps → Share → Copy link');
  };

  return (
    <>
      <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
        <label>Location</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={handleGPS} disabled={locLoading} style={{ ...btnStyle, background: '#065F46', color: '#6EE7B7' }}>
            {locLoading ? 'Fetching...' : '📍 Use My Location'}
          </button>
          <input type="text" placeholder="Paste Google Maps link" value={linkInput} onChange={e => setLinkInput(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
          <button type="button" onClick={handleLink} disabled={!linkInput.trim()} style={btnStyle}>Extract</button>
        </div>
        {(lat || lng) && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#6EE7B7' }}>Coordinates set: {lat}, {lng}</p>}
      </div>
      <div className="admin-modal-field">
        <label>Latitude</label>
        <input type="text" value={lat || ''} onChange={e => onChangeLat(e.target.value)} placeholder="e.g. 19.0760" />
      </div>
      <div className="admin-modal-field">
        <label>Longitude</label>
        <input type="text" value={lng || ''} onChange={e => onChangeLng(e.target.value)} placeholder="e.g. 72.8777" />
      </div>
    </>
  );
}

const CATEGORIES = ['All','Restaurant','Cafe','Saloon','Fruits','Electronics','Hardware','Car','Autoparts','Medical','Hospitals','Gym','Individual','Entertainment','Footwear','Textiles','Others'];
const SHOP_CATEGORIES = CATEGORIES.filter(c => c !== 'All');

const PROFESSIONS = ['All','Pandit','Advocate','CA','Teacher','Doctor','Electrician','Plumber','Photographer','Event Organizer','Carpenter','Car Wash','Maid','Cook','Society Car Wash','Other'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('shoplogintoken');
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  const [activeTab, setActiveTab] = useState('shops');

  // Shops list
  const [shops, setShops] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stats, setStats] = useState({ shops: 0, offers: 0, menus: 0, users: 0, providers: 0 });

  // Shop detail modal
  const [detailShop, setDetailShop] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [shopMenus, setShopMenus] = useState([]);
  const [shopOffers, setShopOffers] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Add menu form
  const [addMenuForm, setAddMenuForm] = useState({ itemName: '', itemPrice: '', itemDescription: '', category: '', itemImage: '' });
  const [addingMenu, setAddingMenu] = useState(false);

  // Add offer form
  const [addOfferForm, setAddOfferForm] = useState({ title: '', description: '', percentage: '', validFrom: '', validTill: '' });
  const [addingOffer, setAddingOffer] = useState(false);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Onboard shop modal
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboardForm, setOnboardForm] = useState({ ownerName: '', shopName: '', contactNumber: '', email: '', category: 'Restaurant', address: '', latitude: '', longitude: '', shopImage: '' });
  const [onboarding, setOnboarding] = useState(false);

  // Agents
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [agentForm, setAgentForm] = useState({ name: '', email: '', role: 'agent' });
  const [addingAgent, setAddingAgent] = useState(false);

  // Providers
  const [providerList, setProviderList] = useState([]);
  const [provTotal, setProvTotal] = useState(0);
  const [provPage, setProvPage] = useState(1);
  const [provTotalPages, setProvTotalPages] = useState(1);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingMoreProviders, setLoadingMoreProviders] = useState(false);
  const [provProfession, setProvProfession] = useState('All');
  const [provSearchInput, setProvSearchInput] = useState('');
  const [provSearch, setProvSearch] = useState('');
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [addProviderForm, setAddProviderForm] = useState({ name: '', profession: 'Doctor', contactNumber: '', email: '', areasServed: '', description: '', experience: '', feeRange: '', languages: '', availability: '', photo: '' });
  const [addingProvider, setAddingProvider] = useState(false);
  const [detailProvider, setDetailProvider] = useState(null);
  const [editProviderForm, setEditProviderForm] = useState({});
  const [savingProvider, setSavingProvider] = useState(false);
  const [featureNote, setFeatureNote] = useState('');
  const [deleteProv, setDeleteProv] = useState(null);

  // Malls
  const [malls, setMalls] = useState([]);
  const [loadingMalls, setLoadingMalls] = useState(false);
  const [mallForm, setMallForm] = useState({ mallName: '', mallAddress: '', mallCity: '', mallImage: '', description: '', latitude: '', longitude: '' });
  const [savingMall, setSavingMall] = useState(false);
  const [editMall, setEditMall] = useState(null);
  const [assignForm, setAssignForm] = useState({ shopId: '', mallId: '', floor: '', shopUnit: '' });
  const [assigning, setAssigning] = useState(false);
  const [allShops, setAllShops] = useState([]);

  const header = { Authorization: token };

  useEffect(() => {
    if (!token || localStorage.getItem('isAdmin') !== 'true') {
      navigate('/shoplogin');
    }
  }, [token, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      const res = await commonrequest('GET', `${BACKEND_URL}/nearbe/admin/stats`, {}, header);
      if (res?.data) setStats(res.data);
    };
    fetchStats();
  }, []);

  const fetchShops = useCallback(async (p = 1) => {
    p === 1 ? setLoading(true) : setLoadingMore(true);
    const url = `${BACKEND_URL}/nearbe/admin/shops?page=${p}&limit=12&category=${activeCategory}&search=${searchTerm}`;
    const res = await commonrequest('GET', url, {}, header);
    if (res?.data) {
      const { shops: newShops, total: t, totalPages: tp } = res.data;
      setShops(prev => p === 1 ? newShops : [...prev, ...newShops]);
      setTotal(t);
      setTotalPages(tp);
      setPage(p);
    } else {
      toast.error('Failed to load shops');
    }
    p === 1 ? setLoading(false) : setLoadingMore(false);
  }, [activeCategory, searchTerm]);

  useEffect(() => { fetchShops(1); }, [activeCategory, searchTerm]);

  const handleSearch = (e) => { e.preventDefault(); setSearchTerm(searchInput.trim()); };
  const handleLoadMore = () => fetchShops(page + 1);

  // Open shop detail
  const openDetail = async (shop) => {
    setDetailShop(shop);
    setDetailTab('info');
    setEditForm({
      shopName: shop.shopName || '',
      ownerName: shop.ownerName || '',
      email: shop.email || '',
      contactNumber: shop.contactNumber || '',
      category: shop.category || '',
      address: shop.address || '',
      latitude: shop.latitude || '',
      longitude: shop.longitude || '',
      shopImage: shop.shopImage || '',
      reelLink: shop.reelLink || '',
    });
    setShopMenus([]);
    setShopOffers([]);
    setLoadingDetail(true);
    const res = await commonrequest('GET', `${BACKEND_URL}/nearbe/admin/shops/${shop._id}`, {}, header);
    if (res?.data) {
      setShopMenus(res.data.menus || []);
      setShopOffers(res.data.offers || []);
    }
    setLoadingDetail(false);
  };

  const closeDetail = () => {
    setDetailShop(null);
    setShopMenus([]);
    setShopOffers([]);
    setAddMenuForm({ itemName: '', itemPrice: '', itemDescription: '', category: '', itemImage: '' });
    setAddOfferForm({ title: '', description: '', percentage: '', validFrom: '', validTill: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await commonrequest('PUT', `${BACKEND_URL}/nearbe/admin/shops/${detailShop._id}`, editForm, header);
    if (res?.data?.shop) {
      setShops(prev => prev.map(s => s._id === detailShop._id ? res.data.shop : s));
      setDetailShop(res.data.shop);
      toast.success('Shop updated');
    } else {
      toast.error('Update failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const res = await commonrequest('DELETE', `${BACKEND_URL}/nearbe/admin/shops/${id}`, {}, header);
    if (res?.data?.message) {
      setShops(prev => prev.filter(s => s._id !== id));
      setTotal(t => t - 1);
      toast.success('Shop deleted');
      closeDetail();
    } else {
      toast.error('Delete failed');
    }
    setDeleteConfirm(null);
  };

  // Menu
  const handleAddMenu = async () => {
    if (!addMenuForm.itemName || !addMenuForm.itemPrice) return toast.error('Item name and price required');
    setAddingMenu(true);
    const res = await commonrequest('POST', `${BACKEND_URL}/nearbe/admin/shops/${detailShop._id}/menu`, addMenuForm, header);
    if (res?.data?.menu) {
      setShopMenus(prev => [...prev, res.data.menu]);
      setAddMenuForm({ itemName: '', itemPrice: '', itemDescription: '', category: '', itemImage: '' });
      toast.success('Menu item added');
    } else {
      toast.error('Failed to add menu item');
    }
    setAddingMenu(false);
  };

  const handleDeleteMenu = async (menuId) => {
    const res = await commonrequest('DELETE', `${BACKEND_URL}/nearbe/admin/menu/${menuId}`, {}, header);
    if (res?.data?.message) {
      setShopMenus(prev => prev.filter(m => m._id !== menuId));
    } else {
      toast.error('Failed to delete menu item');
    }
  };

  // Offers
  const handleAddOffer = async () => {
    if (!addOfferForm.title || !addOfferForm.percentage) return toast.error('Title and percentage required');
    setAddingOffer(true);
    const res = await commonrequest('POST', `${BACKEND_URL}/nearbe/admin/shops/${detailShop._id}/offers`, addOfferForm, header);
    if (res?.data?.offer) {
      setShopOffers(prev => [...prev, res.data.offer]);
      setAddOfferForm({ title: '', description: '', percentage: '', validFrom: '', validTill: '' });
      toast.success('Offer added');
    } else {
      toast.error('Failed to add offer');
    }
    setAddingOffer(false);
  };

  const handleDeleteOffer = async (offerId) => {
    const res = await commonrequest('DELETE', `${BACKEND_URL}/nearbe/admin/offers/${offerId}`, {}, header);
    if (res?.data?.message) {
      setShopOffers(prev => prev.filter(o => o._id !== offerId));
    } else {
      toast.error('Failed to delete offer');
    }
  };

  // Onboard shop
  const handleOnboard = async () => {
    const { ownerName, shopName, email, category, address, shopImage } = onboardForm;
    if (!ownerName || !shopName || !email || !category || !address || !shopImage) {
      return toast.error('All fields marked * are required');
    }
    setOnboarding(true);
    const res = await commonrequest('POST', `${BACKEND_URL}/nearbe/admin/shops`, onboardForm, header);
    if (res?.data?.shop) {
      setShops(prev => [res.data.shop, ...prev]);
      setTotal(t => t + 1);
      setShowOnboard(false);
      setOnboardForm({ ownerName: '', shopName: '', contactNumber: '', email: '', category: 'Restaurant', address: '', latitude: '', longitude: '', shopImage: '' });
      toast.success('Shop onboarded successfully');
    } else {
      toast.error(res?.error || 'Failed to onboard shop');
    }
    setOnboarding(false);
  };

  // Agents
  const fetchAgents = useCallback(async () => {
    setLoadingAgents(true);
    const res = await commonrequest('GET', `${BACKEND_URL}/nearbe/admin/agents`, {}, header);
    if (res?.data?.agents) setAgents(res.data.agents);
    setLoadingAgents(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'agents' && isSuperAdmin) fetchAgents();
  }, [activeTab, isSuperAdmin, fetchAgents]);

  const handleAddAgent = async () => {
    if (!agentForm.name || !agentForm.email) return toast.error('Name and email required');
    setAddingAgent(true);
    const res = await commonrequest('POST', `${BACKEND_URL}/nearbe/admin/agents`, agentForm, header);
    if (res?.data?.agent) {
      setAgents(prev => [res.data.agent, ...prev]);
      setShowAddAgent(false);
      setAgentForm({ name: '', email: '', role: 'agent' });
      toast.success('Agent added');
    } else {
      toast.error(res?.error || 'Failed to add agent');
    }
    setAddingAgent(false);
  };

  const handleDeleteAgent = async (id) => {
    const res = await commonrequest('DELETE', `${BACKEND_URL}/nearbe/admin/agents/${id}`, {}, header);
    if (res?.data?.message) {
      setAgents(prev => prev.filter(a => a._id !== id));
      toast.success('Agent removed');
    } else {
      toast.error('Failed to remove agent');
    }
  };

  // ── Provider handlers ─────────────────────────────────────────────────────
  const fetchProviders = useCallback(async (p = 1) => {
    p === 1 ? setLoadingProviders(true) : setLoadingMoreProviders(true);
    const url = `${BACKEND_URL}/nearbe/admin/providers?page=${p}&limit=12&profession=${provProfession}&search=${provSearch}`;
    const res = await commonrequest('GET', url, {}, header);
    if (res?.data) {
      const { providers, total: t, totalPages: tp } = res.data;
      setProviderList(prev => p === 1 ? providers : [...prev, ...providers]);
      setProvTotal(t);
      setProvTotalPages(tp);
      setProvPage(p);
    } else {
      toast.error('Failed to load providers');
    }
    p === 1 ? setLoadingProviders(false) : setLoadingMoreProviders(false);
  }, [provProfession, provSearch]);

  useEffect(() => {
    if (activeTab === 'providers') fetchProviders(1);
  }, [activeTab, provProfession, provSearch, fetchProviders]);

  const handleProviderSearch = (e) => { e.preventDefault(); setProvSearch(provSearchInput.trim()); };

  const handleAddProvider = async () => {
    if (!addProviderForm.name || !addProviderForm.profession) return toast.error('Name and profession are required');
    setAddingProvider(true);
    const res = await commonrequest('POST', `${BACKEND_URL}/nearbe/admin/providers`, addProviderForm, header);
    if (res?.data?.provider) {
      setProviderList(prev => [res.data.provider, ...prev]);
      setProvTotal(t => t + 1);
      setShowAddProvider(false);
      setAddProviderForm({ name: '', profession: 'Doctor', contactNumber: '', email: '', areasServed: '', description: '', experience: '', feeRange: '', languages: '', availability: '', photo: '' });
      toast.success('Provider onboarded');
    } else {
      toast.error(res?.error || 'Failed to onboard provider');
    }
    setAddingProvider(false);
  };

  const openProviderDetail = (provider) => {
    setDetailProvider(provider);
    setFeatureNote(provider.promotionalNote || '');
    setEditProviderForm({
      name: provider.name || '',
      profession: provider.profession || '',
      contactNumber: provider.contactNumber || '',
      email: provider.email || '',
      areasServed: Array.isArray(provider.areasServed) ? provider.areasServed.join(', ') : (provider.areasServed || ''),
      description: provider.description || '',
      experience: provider.experience || '',
      feeRange: provider.feeRange || '',
      languages: Array.isArray(provider.languages) ? provider.languages.join(', ') : (provider.languages || ''),
      availability: provider.availability || '',
      photo: provider.photo || '',
    });
  };

  const handleSaveProvider = async () => {
    setSavingProvider(true);
    const res = await commonrequest('PUT', `${BACKEND_URL}/nearbe/admin/providers/${detailProvider._id}`, editProviderForm, header);
    if (res?.data?.provider) {
      setProviderList(prev => prev.map(p => p._id === detailProvider._id ? res.data.provider : p));
      setDetailProvider(res.data.provider);
      setFeatureNote(res.data.provider.promotionalNote || '');
      toast.success('Provider updated');
    } else {
      toast.error('Update failed');
    }
    setSavingProvider(false);
  };

  const handleVerifyProvider = async (provider) => {
    const res = await commonrequest('PATCH', `${BACKEND_URL}/nearbe/admin/providers/${provider._id}/verify`, {}, header);
    if (res?.data?.provider) {
      setProviderList(prev => prev.map(p => p._id === provider._id ? res.data.provider : p));
      if (detailProvider?._id === provider._id) setDetailProvider(res.data.provider);
      toast.success(res.data.isVerified ? 'Provider verified' : 'Verification removed');
    } else {
      toast.error('Failed to toggle verification');
    }
  };

  const handleFeatureProvider = async (provider) => {
    const res = await commonrequest('PATCH', `${BACKEND_URL}/nearbe/admin/providers/${provider._id}/feature`, { promotionalNote: featureNote }, header);
    if (res?.data?.provider) {
      setProviderList(prev => prev.map(p => p._id === provider._id ? res.data.provider : p));
      if (detailProvider?._id === provider._id) setDetailProvider(res.data.provider);
      toast.success(res.data.isFeatured ? 'Provider featured' : 'Feature removed');
    } else {
      toast.error('Failed to toggle feature');
    }
  };

  const handleDeleteProvider = async (id) => {
    const res = await commonrequest('DELETE', `${BACKEND_URL}/nearbe/admin/providers/${id}`, {}, header);
    if (res?.data?.message) {
      setProviderList(prev => prev.filter(p => p._id !== id));
      setProvTotal(t => t - 1);
      setDetailProvider(null);
      toast.success('Provider deleted');
    } else {
      toast.error('Delete failed');
    }
    setDeleteProv(null);
  };

  const fetchMalls = useCallback(async () => {
    setLoadingMalls(true);
    const res = await fetch(`${BACKEND_URL}/nearbe/getallmalls`);
    const data = await res.json();
    setMalls(data.malls || []);
    setLoadingMalls(false);
  }, []);

  const fetchAllShopsForAssign = useCallback(async () => {
    const res = await commonrequest('GET', `${BACKEND_URL}/nearbe/admin/shops?page=1&limit=200`, {}, header);
    if (res?.data?.shops) setAllShops(res.data.shops);
  }, []);

  useEffect(() => {
    if (activeTab === 'malls') {
      fetchMalls();
      fetchAllShopsForAssign();
    }
  }, [activeTab, fetchMalls, fetchAllShopsForAssign]);

  const handleCreateMall = async () => {
    const { mallName, mallAddress, mallCity } = mallForm;
    if (!mallName || !mallAddress || !mallCity) return toast.error('Mall name, address and city are required');
    setSavingMall(true);
    const res = await commonrequest('POST', `${BACKEND_URL}/nearbe/admin/malls`, mallForm, header);
    if (res?.data?.mall) {
      setMalls(prev => [{ ...res.data.mall, shopCount: 0, offerCount: 0 }, ...prev]);
      setMallForm({ mallName: '', mallAddress: '', mallCity: '', mallImage: '', description: '', latitude: '', longitude: '' });
      toast.success('Mall created');
    } else {
      toast.error(res?.error || 'Failed to create mall');
    }
    setSavingMall(false);
  };

  const handleUpdateMall = async () => {
    if (!editMall) return;
    setSavingMall(true);
    const res = await commonrequest('PUT', `${BACKEND_URL}/nearbe/admin/malls/${editMall._id}`, editMall, header);
    if (res?.data?.mall) {
      setMalls(prev => prev.map(m => m._id === editMall._id ? { ...res.data.mall, shopCount: editMall.shopCount, offerCount: editMall.offerCount } : m));
      setEditMall(null);
      toast.success('Mall updated');
    } else {
      toast.error('Failed to update mall');
    }
    setSavingMall(false);
  };

  const handleDeleteMall = async (mallId) => {
    const res = await commonrequest('DELETE', `${BACKEND_URL}/nearbe/admin/malls/${mallId}`, {}, header);
    if (res?.data?.message) {
      setMalls(prev => prev.filter(m => m._id !== mallId));
      toast.success('Mall deleted');
    } else {
      toast.error('Failed to delete mall');
    }
  };

  const handleAssignShop = async () => {
    if (!assignForm.shopId) return toast.error('Select a shop');
    setAssigning(true);
    const payload = {
      shopId: assignForm.shopId,
      mallId: assignForm.mallId || null,
      floor: assignForm.floor || null,
      shopUnit: assignForm.shopUnit || null,
    };
    const res = await commonrequest('PUT', `${BACKEND_URL}/nearbe/admin/assignshopmall`, payload, header);
    if (res?.data?.shop) {
      toast.success(assignForm.mallId ? 'Shop assigned to mall' : 'Shop removed from mall');
      setAssignForm({ shopId: '', mallId: '', floor: '', shopUnit: '' });
      fetchMalls();
    } else {
      toast.error('Failed to assign shop');
    }
    setAssigning(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('shoplogintoken');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('isSuperAdmin');
    navigate('/shoplogin');
    toast.success('Logged out');
  };

  return (
    <div className="admin-root">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <span className="admin-logo">Nearbeee</span>
          <span className="admin-badge">{isSuperAdmin ? 'Super Admin' : 'Admin'}</span>
        </div>
        <div className="admin-stats-row">
          <div className="admin-stat"><span className="stat-num">{stats.shops}</span><span className="stat-label">Shops</span></div>
          <div className="admin-stat"><span className="stat-num">{stats.offers}</span><span className="stat-label">Offers</span></div>
          <div className="admin-stat"><span className="stat-num">{stats.menus}</span><span className="stat-label">Menu Items</span></div>
          <div className="admin-stat"><span className="stat-num">{stats.users}</span><span className="stat-label">Users</span></div>
          <div className="admin-stat"><span className="stat-num">{stats.providers}</span><span className="stat-label">Providers</span></div>
        </div>
        <button className="admin-logout-btn" onClick={handleLogout}>Logout</button>
      </header>

      {/* Main tabs */}
      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'shops' ? 'active' : ''}`} onClick={() => setActiveTab('shops')}>Shops</button>
        <button className={`admin-tab ${activeTab === 'malls' ? 'active' : ''}`} onClick={() => setActiveTab('malls')}>🏬 Malls</button>
        <button className={`admin-tab ${activeTab === 'providers' ? 'active' : ''}`} onClick={() => setActiveTab('providers')}>Providers</button>
        {isSuperAdmin && (
          <button className={`admin-tab ${activeTab === 'agents' ? 'active' : ''}`} onClick={() => setActiveTab('agents')}>Team</button>
        )}
      </div>

      {/* === SHOPS TAB === */}
      {activeTab === 'shops' && (
        <>
          <div className="admin-controls">
            <div className="admin-controls-top">
              <form className="admin-search-form" onSubmit={handleSearch}>
                <input
                  className="admin-search-input"
                  type="text"
                  placeholder="Search by shop name, owner, email, address..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
                <button type="submit" className="admin-search-btn">Search</button>
                {searchTerm && (
                  <button type="button" className="admin-clear-btn" onClick={() => { setSearchTerm(''); setSearchInput(''); }}>
                    ✕ Clear
                  </button>
                )}
              </form>
              <button className="admin-onboard-btn" onClick={() => setShowOnboard(true)}>+ Onboard Shop</button>
            </div>
            <div className="admin-category-chips">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  className={`admin-chip ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
            <p className="admin-count">{total} shop{total !== 1 ? 's' : ''} found</p>
          </div>

          <div className="admin-grid">
            {loading ? (
              <div className="admin-loading">Loading shops...</div>
            ) : shops.length === 0 ? (
              <div className="admin-empty">No shops found</div>
            ) : (
              shops.map(shop => (
                <div className="admin-card" key={shop._id} onClick={() => openDetail(shop)}>
                  <div className="admin-card-img-wrap">
                    {shop.shopImage ? (
                      <img src={shop.shopImage} alt={shop.shopName} className="admin-card-img" />
                    ) : (
                      <div className="admin-card-img-placeholder">🏪</div>
                    )}
                    <span className="admin-category-badge">{shop.category}</span>
                  </div>
                  <div className="admin-card-body">
                    <h3 className="admin-card-name">{shop.shopName}</h3>
                    <p className="admin-card-owner">👤 {shop.ownerName}</p>
                    <p className="admin-card-email">✉️ {shop.email}</p>
                    <p className="admin-card-addr">📍 {shop.address}</p>
                    {shop.contactNumber && <p className="admin-card-phone">📞 {shop.contactNumber}</p>}
                  </div>
                  <div className="admin-card-hint">Click to manage</div>
                </div>
              ))
            )}
          </div>

          {!loading && page < totalPages && (
            <div className="admin-load-more-wrap">
              <button className="admin-load-more-btn" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : `Load More (${total - shops.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}

      {/* === MALLS TAB === */}
      {activeTab === 'malls' && (
        <div className="admin-agents-section">
          <div className="admin-agents-header">
            <div>
              <h2>Malls</h2>
              <p className="admin-agents-sub">Create malls and assign shops to them.</p>
            </div>
          </div>

          {/* Create mall form */}
          <div className="admin-add-form" style={{ marginBottom: 24 }}>
            <h4>Create New Mall</h4>
            <div className="admin-add-row" style={{ flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              <input placeholder="Mall Name *" value={mallForm.mallName} onChange={e => setMallForm(f => ({ ...f, mallName: e.target.value }))} />
              <input placeholder="Address *" value={mallForm.mallAddress} onChange={e => setMallForm(f => ({ ...f, mallAddress: e.target.value }))} />
              <input placeholder="City *" value={mallForm.mallCity} onChange={e => setMallForm(f => ({ ...f, mallCity: e.target.value }))} />
              <input placeholder="Description" value={mallForm.description} onChange={e => setMallForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="admin-modal-grid" style={{ marginBottom: 12 }}>
              <ImageUpload
                label="Mall Image"
                value={mallForm.mallImage || ''}
                onChange={url => setMallForm(f => ({ ...f, mallImage: url }))}
              />
              <LocationPicker
                lat={mallForm.latitude}
                lng={mallForm.longitude}
                onChangeLat={v => setMallForm(f => ({ ...f, latitude: v }))}
                onChangeLng={v => setMallForm(f => ({ ...f, longitude: v }))}
              />
            </div>
            <button className="admin-modal-save" onClick={handleCreateMall} disabled={savingMall}>
              {savingMall ? 'Creating...' : 'Create Mall'}
            </button>
          </div>

          {/* Assign shop to mall */}
          <div className="admin-add-form" style={{ marginBottom: 24 }}>
            <h4>Assign Shop to Mall</h4>
            <div className="admin-add-row" style={{ flexWrap: 'wrap', gap: 10 }}>
              <select value={assignForm.shopId} onChange={e => setAssignForm(f => ({ ...f, shopId: e.target.value }))} style={{ flex: 1, minWidth: 200 }}>
                <option value="">Select Shop</option>
                {allShops.map(s => (
                  <option key={s._id} value={s._id}>{s.shopName} — {s.ownerName}</option>
                ))}
              </select>
              <select value={assignForm.mallId} onChange={e => setAssignForm(f => ({ ...f, mallId: e.target.value }))} style={{ flex: 1, minWidth: 180 }}>
                <option value="">No Mall (remove)</option>
                {malls.map(m => (
                  <option key={m._id} value={m._id}>{m.mallName}</option>
                ))}
              </select>
              {assignForm.mallId && (
                <>
                  <input
                    placeholder="Floor (e.g. Ground Floor)"
                    value={assignForm.floor}
                    onChange={e => setAssignForm(f => ({ ...f, floor: e.target.value }))}
                    style={{ flex: 1, minWidth: 150 }}
                  />
                  <input
                    placeholder="Unit / Shop No. (e.g. G-12)"
                    value={assignForm.shopUnit}
                    onChange={e => setAssignForm(f => ({ ...f, shopUnit: e.target.value }))}
                    style={{ flex: 1, minWidth: 150 }}
                  />
                </>
              )}
              <button className="admin-modal-save" onClick={handleAssignShop} disabled={assigning}>
                {assigning ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>

          {/* Mall list */}
          {loadingMalls ? (
            <div className="admin-loading">Loading malls...</div>
          ) : malls.length === 0 ? (
            <div className="admin-empty">No malls yet. Create one above.</div>
          ) : (
            <div className="admin-agents-list">
              {malls.map(mall => (
                <div className="admin-agent-row" key={mall._id} style={{ alignItems: 'flex-start' }}>
                  <div className="admin-agent-info" style={{ flexDirection: 'column', gap: 4 }}>
                    {editMall?._id === mall._id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                        <div className="admin-add-row" style={{ flexWrap: 'wrap', gap: 8 }}>
                          <input value={editMall.mallName} onChange={e => setEditMall(m => ({ ...m, mallName: e.target.value }))} placeholder="Mall Name" />
                          <input value={editMall.mallAddress} onChange={e => setEditMall(m => ({ ...m, mallAddress: e.target.value }))} placeholder="Address" />
                          <input value={editMall.mallCity} onChange={e => setEditMall(m => ({ ...m, mallCity: e.target.value }))} placeholder="City" />
                          <input value={editMall.description || ''} onChange={e => setEditMall(m => ({ ...m, description: e.target.value }))} placeholder="Description" />
                        </div>
                        <div className="admin-modal-grid">
                          <ImageUpload
                            label="Mall Image"
                            value={editMall.mallImage || ''}
                            onChange={url => setEditMall(m => ({ ...m, mallImage: url }))}
                          />
                          <LocationPicker
                            lat={editMall.latitude}
                            lng={editMall.longitude}
                            onChangeLat={v => setEditMall(m => ({ ...m, latitude: v }))}
                            onChangeLng={v => setEditMall(m => ({ ...m, longitude: v }))}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="admin-modal-save" onClick={handleUpdateMall} disabled={savingMall}>{savingMall ? 'Saving...' : 'Save'}</button>
                          <button className="admin-modal-cancel" onClick={() => setEditMall(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="admin-agent-name">{mall.mallName}</span>
                        <span className="admin-agent-email">📍 {mall.mallAddress}, {mall.mallCity}</span>
                        <span className="admin-agent-role agent">🏪 {mall.shopCount} shops · 🏷️ {mall.offerCount} offers</span>
                        {mall.description && <span style={{ fontSize: 12, color: '#9CA3AF' }}>{mall.description}</span>}
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="admin-modal-save" style={{ padding: '6px 14px' }} onClick={() => setEditMall({ ...mall })}>Edit</button>
                    <button className="admin-delete-btn" onClick={() => handleDeleteMall(mall._id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === PROVIDERS TAB === */}
      {activeTab === 'providers' && (
        <div className="admin-agents-section">
          <div className="admin-agents-header">
            <div>
              <h2>Service Providers</h2>
              <p className="admin-agents-sub">{provTotal} provider{provTotal !== 1 ? 's' : ''} listed</p>
            </div>
            <button className="admin-onboard-btn" onClick={() => setShowAddProvider(true)}>+ Add Provider</button>
          </div>

          {/* Search + profession filter */}
          <div className="admin-controls-top" style={{ marginBottom: 12 }}>
            <form className="admin-search-form" onSubmit={handleProviderSearch}>
              <input
                className="admin-search-input"
                type="text"
                placeholder="Search by name, profession, email, phone..."
                value={provSearchInput}
                onChange={e => setProvSearchInput(e.target.value)}
              />
              <button type="submit" className="admin-search-btn">Search</button>
              {provSearch && (
                <button type="button" className="admin-clear-btn" onClick={() => { setProvSearch(''); setProvSearchInput(''); }}>✕ Clear</button>
              )}
            </form>
          </div>
          <div className="admin-category-chips" style={{ marginBottom: 16 }}>
            {PROFESSIONS.map(p => (
              <button
                key={p}
                className={`admin-chip ${provProfession === p ? 'active' : ''}`}
                onClick={() => setProvProfession(p)}
              >
                {p}
              </button>
            ))}
          </div>

          {loadingProviders ? (
            <div className="admin-loading">Loading providers...</div>
          ) : providerList.length === 0 ? (
            <div className="admin-empty">No providers found</div>
          ) : (
            <div className="admin-grid">
              {providerList.map(prov => (
                <div className="admin-card" key={prov._id} onClick={() => openProviderDetail(prov)}>
                  <div className="admin-card-img-wrap">
                    {prov.photo ? (
                      <img src={prov.photo} alt={prov.name} className="admin-card-img" />
                    ) : (
                      <div className="admin-card-img-placeholder">👤</div>
                    )}
                    <span className="admin-category-badge">{prov.profession}</span>
                  </div>
                  <div className="admin-card-body">
                    <h3 className="admin-card-name">
                      {prov.name}
                      {prov.isVerified && <span style={{ marginLeft: 6, fontSize: 11, color: '#10B981', fontWeight: 700 }}>✓ Verified</span>}
                      {prov.isFeatured && <span style={{ marginLeft: 6, fontSize: 11, color: '#F97316', fontWeight: 700 }}>★ Featured</span>}
                    </h3>
                    {prov.contactNumber && <p className="admin-card-phone">📞 {prov.contactNumber}</p>}
                    {prov.email && <p className="admin-card-email">✉️ {prov.email}</p>}
                    {prov.areasServed?.length > 0 && <p className="admin-card-addr">📍 {prov.areasServed.join(', ')}</p>}
                    {prov.experience && <p className="admin-card-owner">{prov.experience} yrs exp · {prov.feeRange || '—'}</p>}
                  </div>
                  <div className="admin-card-hint">Click to manage</div>
                </div>
              ))}
            </div>
          )}

          {!loadingProviders && provPage < provTotalPages && (
            <div className="admin-load-more-wrap">
              <button className="admin-load-more-btn" onClick={() => fetchProviders(provPage + 1)} disabled={loadingMoreProviders}>
                {loadingMoreProviders ? 'Loading...' : `Load More (${provTotal - providerList.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* === AGENTS TAB === */}
      {activeTab === 'agents' && isSuperAdmin && (
        <div className="admin-agents-section">
          <div className="admin-agents-header">
            <div>
              <h2>Team Members</h2>
              <p className="admin-agents-sub">Agents can manage shops. Super Admins can manage everything including adding other team members.</p>
            </div>
            <button className="admin-onboard-btn" onClick={() => setShowAddAgent(true)}>+ Add Member</button>
          </div>
          {loadingAgents ? (
            <div className="admin-loading">Loading agents...</div>
          ) : agents.length === 0 ? (
            <div className="admin-empty">No agents yet. Add your first agent.</div>
          ) : (
            <div className="admin-agents-list">
              {agents.map(agent => (
                <div className="admin-agent-row" key={agent._id}>
                  <div className="admin-agent-info">
                    <span className="admin-agent-name">{agent.name}</span>
                    <span className="admin-agent-email">{agent.email}</span>
                    <span className={`admin-agent-role ${agent.role}`}>{agent.role === 'superadmin' ? 'Super Admin' : 'Agent'}</span>
                  </div>
                  <button className="admin-delete-btn" onClick={() => handleDeleteAgent(agent._id)}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === SHOP DETAIL MODAL === */}
      {detailShop && (
        <div className="admin-modal-overlay" onClick={closeDetail}>
          <div className="admin-modal admin-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{detailShop.shopName}</h2>
              <div className="admin-modal-header-actions">
                <button className="admin-delete-btn" onClick={() => setDeleteConfirm(detailShop)}>Delete Shop</button>
                <button className="admin-modal-close" onClick={closeDetail}>✕</button>
              </div>
            </div>

            <div className="admin-detail-tabs">
              <button className={`admin-detail-tab ${detailTab === 'info' ? 'active' : ''}`} onClick={() => setDetailTab('info')}>Info</button>
              <button className={`admin-detail-tab ${detailTab === 'menu' ? 'active' : ''}`} onClick={() => setDetailTab('menu')}>
                Menu {shopMenus.length > 0 && `(${shopMenus.length})`}
              </button>
              <button className={`admin-detail-tab ${detailTab === 'offers' ? 'active' : ''}`} onClick={() => setDetailTab('offers')}>
                Offers {shopOffers.length > 0 && `(${shopOffers.length})`}
              </button>
            </div>

            {loadingDetail && <div className="admin-detail-loading">Loading...</div>}

            {/* Info tab */}
            {detailTab === 'info' && !loadingDetail && (
              <>
                {editForm.shopImage && <img src={editForm.shopImage} alt="Shop" className="admin-modal-img" />}
                <div className="admin-modal-grid">
                  {[
                    { label: 'Shop Name', field: 'shopName' },
                    { label: 'Owner Name', field: 'ownerName' },
                    { label: 'Email', field: 'email' },
                    { label: 'Contact Number', field: 'contactNumber' },
                    { label: 'Address', field: 'address' },
                    { label: 'Reel Link (optional)', field: 'reelLink' },
                  ].map(({ label, field }) => (
                    <div className="admin-modal-field" key={field}>
                      <label>{label}</label>
                      <input
                        type="text"
                        value={editForm[field] || ''}
                        onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                      />
                    </div>
                  ))}
                  <div className="admin-modal-field">
                    <label>Category</label>
                    <select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}>
                      {SHOP_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <ImageUpload
                    label="Shop Image *"
                    value={editForm.shopImage || ''}
                    onChange={url => setEditForm(f => ({ ...f, shopImage: url }))}
                  />
                  <LocationPicker
                    lat={editForm.latitude}
                    lng={editForm.longitude}
                    onChangeLat={v => setEditForm(f => ({ ...f, latitude: v }))}
                    onChangeLng={v => setEditForm(f => ({ ...f, longitude: v }))}
                  />
                </div>
                <div className="admin-modal-footer">
                  <button className="admin-modal-cancel" onClick={closeDetail}>Cancel</button>
                  <button className="admin-modal-save" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}

            {/* Menu tab */}
            {detailTab === 'menu' && !loadingDetail && (
              <div className="admin-list-section">
                <div className="admin-add-form">
                  <h4>Add Menu Item</h4>
                  <div className="admin-add-row">
                    <input placeholder="Item Name *" value={addMenuForm.itemName} onChange={e => setAddMenuForm(f => ({ ...f, itemName: e.target.value }))} />
                    <input placeholder="Price (₹) *" type="number" value={addMenuForm.itemPrice} onChange={e => setAddMenuForm(f => ({ ...f, itemPrice: e.target.value }))} />
                    <input placeholder="Category" value={addMenuForm.category} onChange={e => setAddMenuForm(f => ({ ...f, category: e.target.value }))} />
                    <input placeholder="Description" value={addMenuForm.itemDescription} onChange={e => setAddMenuForm(f => ({ ...f, itemDescription: e.target.value }))} />
                    <input placeholder="Image URL" value={addMenuForm.itemImage} onChange={e => setAddMenuForm(f => ({ ...f, itemImage: e.target.value }))} />
                    <button className="admin-modal-save" onClick={handleAddMenu} disabled={addingMenu}>
                      {addingMenu ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </div>
                {shopMenus.length === 0 ? (
                  <p className="admin-empty-list">No menu items yet</p>
                ) : (
                  <div className="admin-items-list">
                    {shopMenus.map(item => (
                      <div className="admin-item-row" key={item._id}>
                        {item.itemImage && <img src={item.itemImage} alt={item.itemName} className="admin-item-img" />}
                        <div className="admin-item-info">
                          <span className="admin-item-name">{item.itemName}</span>
                          <span className="admin-item-price">₹{item.itemPrice}</span>
                          {item.category && <span className="admin-item-cat">{item.category}</span>}
                          {item.itemDescription && <span className="admin-item-desc">{item.itemDescription}</span>}
                        </div>
                        <button className="admin-delete-btn" onClick={() => handleDeleteMenu(item._id)}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Offers tab */}
            {detailTab === 'offers' && !loadingDetail && (
              <div className="admin-list-section">
                <div className="admin-add-form">
                  <h4>Add Offer</h4>
                  <div className="admin-add-row">
                    <input placeholder="Title *" value={addOfferForm.title} onChange={e => setAddOfferForm(f => ({ ...f, title: e.target.value }))} />
                    <input placeholder="Discount % *" type="number" value={addOfferForm.percentage} onChange={e => setAddOfferForm(f => ({ ...f, percentage: e.target.value }))} />
                    <input placeholder="Description" value={addOfferForm.description} onChange={e => setAddOfferForm(f => ({ ...f, description: e.target.value }))} />
                    <input placeholder="Valid From (YYYY-MM-DD)" value={addOfferForm.validFrom} onChange={e => setAddOfferForm(f => ({ ...f, validFrom: e.target.value }))} />
                    <input placeholder="Valid Till (YYYY-MM-DD)" value={addOfferForm.validTill} onChange={e => setAddOfferForm(f => ({ ...f, validTill: e.target.value }))} />
                    <button className="admin-modal-save" onClick={handleAddOffer} disabled={addingOffer}>
                      {addingOffer ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </div>
                {shopOffers.length === 0 ? (
                  <p className="admin-empty-list">No offers yet</p>
                ) : (
                  <div className="admin-items-list">
                    {shopOffers.map(offer => (
                      <div className="admin-item-row" key={offer._id}>
                        <div className="admin-item-info">
                          <span className="admin-item-name">{offer.title}</span>
                          <span className="admin-item-price">{offer.percentage}% off</span>
                          {offer.description && <span className="admin-item-desc">{offer.description}</span>}
                          {offer.validTill && <span className="admin-item-cat">Till {new Date(offer.validTill).toLocaleDateString()}</span>}
                        </div>
                        <button className="admin-delete-btn" onClick={() => handleDeleteOffer(offer._id)}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* === ONBOARD SHOP MODAL === */}
      {showOnboard && (
        <div className="admin-modal-overlay" onClick={() => setShowOnboard(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Onboard New Shop</h2>
              <button className="admin-modal-close" onClick={() => setShowOnboard(false)}>✕</button>
            </div>
            <div className="admin-modal-grid">
              {[
                { label: 'Owner Name *', field: 'ownerName' },
                { label: 'Shop Name *', field: 'shopName' },
                { label: 'Owner Email *', field: 'email' },
                { label: 'Contact Number', field: 'contactNumber' },
                { label: 'Address *', field: 'address' },
              ].map(({ label, field }) => (
                <div className="admin-modal-field" key={field}>
                  <label>{label}</label>
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    value={onboardForm[field] || ''}
                    onChange={e => setOnboardForm(f => ({ ...f, [field]: e.target.value }))}
                  />
                </div>
              ))}
              <div className="admin-modal-field">
                <label>Category *</label>
                <select value={onboardForm.category} onChange={e => setOnboardForm(f => ({ ...f, category: e.target.value }))}>
                  {SHOP_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <ImageUpload
                label="Shop Image *"
                value={onboardForm.shopImage || ''}
                onChange={url => setOnboardForm(f => ({ ...f, shopImage: url }))}
              />
              <LocationPicker
                lat={onboardForm.latitude}
                lng={onboardForm.longitude}
                onChangeLat={v => setOnboardForm(f => ({ ...f, latitude: v }))}
                onChangeLng={v => setOnboardForm(f => ({ ...f, longitude: v }))}
              />
            </div>
            <p className="admin-onboard-note">
              The shop owner can log in via OTP at /shoplogin using the email above after onboarding.
            </p>
            <div className="admin-modal-footer">
              <button className="admin-modal-cancel" onClick={() => setShowOnboard(false)}>Cancel</button>
              <button className="admin-modal-save" onClick={handleOnboard} disabled={onboarding}>
                {onboarding ? 'Onboarding...' : 'Onboard Shop'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === ADD TEAM MEMBER MODAL === */}
      {showAddAgent && (
        <div className="admin-modal-overlay" onClick={() => setShowAddAgent(false)}>
          <div className="admin-modal admin-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Add Team Member</h2>
              <button className="admin-modal-close" onClick={() => setShowAddAgent(false)}>✕</button>
            </div>
            <div className="admin-modal-grid">
              <div className="admin-modal-field">
                <label>Full Name *</label>
                <input type="text" placeholder="Member's full name" value={agentForm.name} onChange={e => setAgentForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="admin-modal-field">
                <label>Email *</label>
                <input type="email" placeholder="member@example.com" value={agentForm.email} onChange={e => setAgentForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="admin-modal-field">
                <label>Role</label>
                <select value={agentForm.role} onChange={e => setAgentForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="agent">Agent — can manage shops, providers, malls</option>
                  <option value="superadmin">Super Admin — full access including team management</option>
                </select>
              </div>
            </div>
            <p className="admin-onboard-note">The member will log in via OTP at /shoplogin using this email.</p>
            <div className="admin-modal-footer">
              <button className="admin-modal-cancel" onClick={() => setShowAddAgent(false)}>Cancel</button>
              <button className="admin-modal-save" onClick={handleAddAgent} disabled={addingAgent}>
                {addingAgent ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === ADD PROVIDER MODAL === */}
      {showAddProvider && (
        <div className="admin-modal-overlay" onClick={() => setShowAddProvider(false)}>
          <div className="admin-modal admin-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Add Service Provider</h2>
              <button className="admin-modal-close" onClick={() => setShowAddProvider(false)}>✕</button>
            </div>
            <div className="admin-modal-grid">
              {[
                { label: 'Full Name *', field: 'name' },
                { label: 'Contact Number', field: 'contactNumber' },
                { label: 'Email', field: 'email' },
                { label: 'Experience (years)', field: 'experience' },
                { label: 'Fee Range (e.g. ₹500–₹1500)', field: 'feeRange' },
                { label: 'Areas Served (comma-separated)', field: 'areasServed' },
                { label: 'Languages (comma-separated)', field: 'languages' },
                { label: 'Availability (e.g. Mon–Sat 9am–6pm)', field: 'availability' },
              ].map(({ label, field }) => (
                <div className="admin-modal-field" key={field}>
                  <label>{label}</label>
                  <input type="text" value={addProviderForm[field] || ''} onChange={e => setAddProviderForm(f => ({ ...f, [field]: e.target.value }))} />
                </div>
              ))}
              <ImageUpload
                label="Photo"
                value={addProviderForm.photo || ''}
                onChange={url => setAddProviderForm(f => ({ ...f, photo: url }))}
              />
              <div className="admin-modal-field">
                <label>Profession *</label>
                <select value={addProviderForm.profession} onChange={e => setAddProviderForm(f => ({ ...f, profession: e.target.value }))}>
                  {PROFESSIONS.filter(p => p !== 'All').map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea
                  rows={3}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: '#1F2937', color: '#F9FAFB', resize: 'vertical' }}
                  value={addProviderForm.description || ''}
                  onChange={e => setAddProviderForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-modal-cancel" onClick={() => setShowAddProvider(false)}>Cancel</button>
              <button className="admin-modal-save" onClick={handleAddProvider} disabled={addingProvider}>
                {addingProvider ? 'Adding...' : 'Add Provider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === PROVIDER DETAIL MODAL === */}
      {detailProvider && (
        <div className="admin-modal-overlay" onClick={() => setDetailProvider(null)}>
          <div className="admin-modal admin-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{detailProvider.name}</h2>
              <div className="admin-modal-header-actions">
                <button className="admin-delete-btn" onClick={() => setDeleteProv(detailProvider)}>Delete</button>
                <button className="admin-modal-close" onClick={() => setDetailProvider(null)}>✕</button>
              </div>
            </div>

            {/* Verify + Feature toggles */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <button
                className={detailProvider.isVerified ? 'admin-delete-btn' : 'admin-modal-save'}
                style={{ padding: '8px 18px' }}
                onClick={() => handleVerifyProvider(detailProvider)}
              >
                {detailProvider.isVerified ? 'Remove Verification' : 'Mark as Verified'}
              </button>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                <input
                  type="text"
                  placeholder="Promotional note (optional)"
                  value={featureNote}
                  onChange={e => setFeatureNote(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: '#1F2937', color: '#F9FAFB', fontSize: 13 }}
                />
                <button
                  className={detailProvider.isFeatured ? 'admin-delete-btn' : 'admin-modal-save'}
                  style={{ padding: '8px 18px', whiteSpace: 'nowrap' }}
                  onClick={() => handleFeatureProvider(detailProvider)}
                >
                  {detailProvider.isFeatured ? 'Unfeature' : 'Feature'}
                </button>
              </div>
            </div>

            {/* Edit fields */}
            <div className="admin-modal-grid">
              {[
                { label: 'Full Name', field: 'name' },
                { label: 'Contact Number', field: 'contactNumber' },
                { label: 'Email', field: 'email' },
                { label: 'Experience (years)', field: 'experience' },
                { label: 'Fee Range', field: 'feeRange' },
                { label: 'Areas Served (comma-separated)', field: 'areasServed' },
                { label: 'Languages (comma-separated)', field: 'languages' },
                { label: 'Availability', field: 'availability' },
              ].map(({ label, field }) => (
                <div className="admin-modal-field" key={field}>
                  <label>{label}</label>
                  <input type="text" value={editProviderForm[field] || ''} onChange={e => setEditProviderForm(f => ({ ...f, [field]: e.target.value }))} />
                </div>
              ))}
              <ImageUpload
                label="Photo"
                value={editProviderForm.photo || ''}
                onChange={url => setEditProviderForm(f => ({ ...f, photo: url }))}
              />
              <div className="admin-modal-field">
                <label>Profession</label>
                <select value={editProviderForm.profession || ''} onChange={e => setEditProviderForm(f => ({ ...f, profession: e.target.value }))}>
                  {PROFESSIONS.filter(p => p !== 'All').map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea
                  rows={3}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: '#1F2937', color: '#F9FAFB', resize: 'vertical' }}
                  value={editProviderForm.description || ''}
                  onChange={e => setEditProviderForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-modal-cancel" onClick={() => setDetailProvider(null)}>Cancel</button>
              <button className="admin-modal-save" onClick={handleSaveProvider} disabled={savingProvider}>
                {savingProvider ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === DELETE PROVIDER CONFIRM === */}
      {deleteProv && (
        <div className="admin-modal-overlay" onClick={() => setDeleteProv(null)}>
          <div className="admin-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete Provider?</h3>
            <p>This will permanently delete <strong>{deleteProv.name}</strong>. This cannot be undone.</p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-cancel" onClick={() => setDeleteProv(null)}>Cancel</button>
              <button className="admin-delete-confirm-btn" onClick={() => handleDeleteProvider(deleteProv._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* === DELETE CONFIRM === */}
      {deleteConfirm && (
        <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="admin-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete Shop?</h3>
            <p>This will permanently delete <strong>{deleteConfirm.shopName}</strong> and all its menu items, offers, and NI vectors. This cannot be undone.</p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="admin-delete-confirm-btn" onClick={() => handleDelete(deleteConfirm._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
